/**
 * views/reporting/sections/RevenueTrend.jsx - the revenue trend chart.
 * Rendered by calling it from ReportingView.jsx with the values it reads,
 * so it renders the same DOM the view used to build inline.
 */
import { ResponsiveContainer, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, Area, Legend } from 'recharts';
import { Icon } from '../../../components/ui/Icon';
import { fmt } from '../../../utils/format';
import { exportCsv } from '../../../utils/exportCsv';
import { exportChartPNG } from '../../../utils/exportChartPNG';
import { CSV_BTN } from '../reportingHelpers';
import { CARD, TICK, TT } from '../reportingTokens';
import { ChartFilter } from '../ChartFilter';
import { SectionHeader } from '../SectionHeader';

export function renderRevenueTrend({
  revFilter,
  setRevFilter,
  revCustomFrom,
  revCustomTo,
  setRevCustomFrom,
  setRevCustomTo,
  revTrendData,
  revTrendRef,
}) {
  return (
    <>
      {/* REVENUE TREND */}
      <SectionHeader
        title="Revenue & Pipeline Trend"
        subtitle="Won revenue vs open pipeline value"
        explain={{
          title: 'Revenue & Pipeline Trend',
          body: 'Two layers per period: WON revenue (solid green) and OPEN pipeline (dashed blue). Past years usually show Won ≫ Pipeline — that\'s expected, because every bid eventually leaves "open" by being won or lost. The current year usually shows Pipeline near its peak with Won still growing.',
          formula:
            "Won  = SUM(quoted_price) where status='Won'\nOpen = SUM(quoted_price) where status='Open'\nGrouped by year (or month for shorter ranges)",
        }}
        right={
          <>
            <ChartFilter
              value={revFilter}
              onChange={setRevFilter}
              showCustom={revFilter === 'custom'}
              customFrom={revCustomFrom}
              customTo={revCustomTo}
              onCustomFromChange={setRevCustomFrom}
              onCustomToChange={setRevCustomTo}
              onApplyCustom={() => {}}
            />
            <button
              onClick={() =>
                exportCsv(
                  'revenue_trend.csv',
                  revTrendData.map((d) => ({ Period: d.period, 'Won Value': d.wonValue, 'Open Value': d.openValue }))
                )
              }
              style={CSV_BTN}
            >
              <Icon name="download" size={10} color="#666" /> CSV
            </button>
            <button onClick={() => exportChartPNG(revTrendRef, 'revenue_trend')} style={CSV_BTN}>
              <Icon name="download" size={10} color="#666" /> PNG
            </button>
          </>
        }
      />
      <div ref={revTrendRef} style={{ ...CARD, marginBottom: 20 }}>
        <div style={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={revTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F4" vertical={false} />
              <XAxis dataKey="period" tick={TICK} axisLine={false} tickLine={false} />
              <YAxis
                tick={TICK}
                axisLine={false}
                tickLine={false}
                width={50}
                tickFormatter={(v) => (v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${v}`)}
              />
              <Tooltip contentStyle={TT} formatter={(v) => [fmt(v), '']} />
              <Area
                type="monotone"
                dataKey="wonValue"
                name="Won Revenue"
                stroke="#1B5E20"
                fill="#1B5E20"
                fillOpacity={0.15}
                strokeWidth={2.5}
              />
              <Area
                type="monotone"
                dataKey="openValue"
                name="Open Pipeline"
                stroke="#1565C0"
                fill="#1565C0"
                fillOpacity={0.08}
                strokeWidth={2}
                strokeDasharray="5 5"
              />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10, fontFamily: 'Montserrat', fontWeight: 600 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}
